import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { MessageSquare, ChevronDown, ChevronRight, Filter, Circle, CheckCircle2 } from "lucide-react";
import { FeedbackService } from "../services/feedbackService";
import { useRealtimeFeedback } from "../hooks/useRealtimeFeedback";
import { Table, Button, Badge, SearchBar, Popover, FilterChip, Dropdown } from './ui';
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import NewFeedbackModal from "./NewFeedbackModal";
import FeedbackDetailsModal from "./FeedbackDetailsModal";

/**
 * FeedbackTable
 *
 * Standardized feedback table using the new UI component library.
 *
 * Features:
 * - Client-side filtering and sorting
 * - Real-time updates
 * - Inline editing for quick changes
 * - Details modal for full view/edit
 * - Collapsible completed section
 * - Type and priority badges
 * - Custom priority sorting (High → Medium → Low)
 */
const FeedbackTable = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(() => {
    return localStorage.getItem('feedback-table-sort-field') || 'priority';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return localStorage.getItem('feedback-table-sort-direction') || 'desc';
  });
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isOpenCollapsed, setIsOpenCollapsed] = useState(false); // Expanded by default
  const [isWorkingCollapsed, setIsWorkingCollapsed] = useState(false); // Expanded by default
  const [isCompletedCollapsed, setIsCompletedCollapsed] = useState(true); // Collapsed by default
  const [searchTerm, setSearchTerm] = useState('');
  // Auto-resize enabled by default, but load from localStorage if previously disabled
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem('feedback-auto-resize');
      return stored === null ? true : stored === 'true';
    } catch (error) {
      return true; // Default to true if localStorage is unavailable
    }
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    priority: '',
    createdBy: ''
  });
  const [toast, setToast] = useState(null); // { message, feedbackId, originalState }
  const [pendingCompletion, setPendingCompletion] = useState(null); // { id, completed }
  const undoTimeoutRef = useRef(null);

  // Load feedback on mount
  useEffect(() => {
    loadFeedback();
  }, []);

  // Save sort state to localStorage
  useEffect(() => {
    localStorage.setItem('feedback-table-sort-field', sortField);
  }, [sortField]);

  useEffect(() => {
    localStorage.setItem('feedback-table-sort-direction', sortDirection);
  }, [sortDirection]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const data = await FeedbackService.getAllFeedback();
      setFeedback(data);
      setError(null);
    } catch (err) {
      console.error("Error loading feedback:", err);
      setError(err.message || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  // Real-time updates
  const handleFeedbackInsert = useCallback((newFeedback) => {
    setFeedback(prev => [newFeedback, ...prev]);
  }, []);

  const handleFeedbackUpdate = useCallback((updatedFeedback) => {
    setFeedback(prev =>
      prev.map(item => item.id === updatedFeedback.id ? updatedFeedback : item)
    );
  }, []);

  const handleFeedbackDelete = useCallback((deletedId) => {
    setFeedback(prev => prev.filter(item => item.id !== deletedId));
  }, []);

  useRealtimeFeedback(
    handleFeedbackInsert,
    handleFeedbackUpdate,
    handleFeedbackDelete
  );

  // Get unique values for filter dropdowns
  const uniqueTypes = useMemo(() => {
    const types = new Set(feedback.map(f => f.type).filter(Boolean));
    return Array.from(types).sort();
  }, [feedback]);

  const uniquePriorities = useMemo(() => {
    const priorities = new Set(feedback.map(f => f.priority).filter(Boolean));
    return Array.from(priorities).sort((a, b) => {
      const order = { 'High': 1, 'Medium': 2, 'Low': 3 };
      return (order[a] || 999) - (order[b] || 999);
    });
  }, [feedback]);

  const uniqueCreatedBy = useMemo(() => {
    const names = new Set(feedback.map(f => f.employees?.name).filter(Boolean));
    return Array.from(names).sort();
  }, [feedback]);

  // Apply filters and search to feedback items
  const filteredFeedback = useMemo(() => {
    let filtered = feedback;

    // Apply filters first
    if (filters.type) {
      filtered = filtered.filter(item => item.type === filters.type);
    }
    if (filters.priority) {
      filtered = filtered.filter(item => item.priority === filters.priority);
    }
    if (filters.createdBy) {
      filtered = filtered.filter(item => item.employees?.name === filters.createdBy);
    }

    // Then apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.request?.toLowerCase().includes(searchLower) ||
        item.details?.toLowerCase().includes(searchLower) ||
        item.type?.toLowerCase().includes(searchLower) ||
        item.priority?.toLowerCase().includes(searchLower) ||
        item.employees?.name?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [feedback, filters, searchTerm]);

  // Separate feedback into three categories based on status
  const openFeedback = useMemo(() =>
    filteredFeedback.filter(item => item.status === 'open'),
    [filteredFeedback]
  );

  const inProgressFeedback = useMemo(() =>
    filteredFeedback.filter(item => item.status === 'in_progress'),
    [filteredFeedback]
  );

  const completedFeedback = useMemo(() =>
    filteredFeedback.filter(item => item.status === 'completed'),
    [filteredFeedback]
  );

  // Custom sorting function
  const sortFeedback = useCallback((items) => {
    return [...items].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle nested employee name
      if (sortField === "created_by") {
        aVal = a.employees?.name || "";
        bVal = b.employees?.name || "";
      }

      // Handle priority sorting with custom order: High → Medium → Low
      if (sortField === "priority") {
        const priorityOrder = { "High": 3, "Medium": 2, "Low": 1 };
        const aOrder = priorityOrder[aVal] || 0;
        const bOrder = priorityOrder[bVal] || 0;
        // Always show High → Medium → Low
        return bOrder - aOrder;
      }

      // Handle null values
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Compare values
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [sortField, sortDirection]);

  const sortedOpenFeedback = useMemo(() => sortFeedback(openFeedback), [openFeedback, sortFeedback]);
  const sortedInProgressFeedback = useMemo(() => sortFeedback(inProgressFeedback), [inProgressFeedback, sortFeedback]);
  const sortedCompletedFeedback = useMemo(() => sortFeedback(completedFeedback), [completedFeedback, sortFeedback]);

  // Toggle section functions
  const toggleOpenSection = () => {
    setIsOpenCollapsed(!isOpenCollapsed);
  };

  const toggleWorkingSection = () => {
    setIsWorkingCollapsed(!isWorkingCollapsed);
  };

  const toggleCompletedSection = () => {
    setIsCompletedCollapsed(!isCompletedCollapsed);
  };

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
      // Persist the preference to localStorage
      try {
        localStorage.setItem('feedback-auto-resize', 'false');
      } catch (error) {
        console.error('Error saving auto-resize preference:', error);
      }
    }
  };

  // Handle auto-resize toggle - persist state to localStorage
  const handleAutoResizeToggle = () => {
    const newValue = !autoResizeEnabled;
    setAutoResizeEnabled(newValue);
    try {
      localStorage.setItem('feedback-auto-resize', String(newValue));
    } catch (error) {
      console.error('Error saving auto-resize preference:', error);
    }
  };

  // Handle sort events from Table component
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Handle row click to open details modal
  const handleRowClick = (feedbackItem) => {
    setSelectedFeedback(feedbackItem);
    setShowDetailsModal(true);
  };

  // Cleanup undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  // Handle undo action
  const handleUndo = () => {
    if (pendingCompletion && toast) {
      // Clear the timeout
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
        undoTimeoutRef.current = null;
      }

      // Revert the optimistic update
      setFeedback(prev =>
        prev.map(item =>
          item.id === toast.feedbackId ? toast.originalState : item
        )
      );

      // Clear states
      setPendingCompletion(null);
      setToast(null);
    }
  };

  // Handle status change with undo functionality
  const handleStatusChange = async (id, oldStatus, newStatus) => {
    // No change, ignore
    if (oldStatus === newStatus) return;

    // Clear any existing undo timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }

    // Find the current item to store its state
    const currentItem = feedback.find(item => item.id === id);
    if (!currentItem) return;

    // Optimistic update
    setFeedback(prev =>
      prev.map(item =>
        item.id === id ? { ...item, status: newStatus } : item
      )
    );

    // Show toast with undo option
    const statusLabels = {
      'open': 'Open',
      'in_progress': 'Working',
      'completed': 'Completed'
    };

    setToast({
      message: `Status changed to ${statusLabels[newStatus]}`,
      feedbackId: id,
      originalState: currentItem
    });

    setPendingCompletion({ id, status: newStatus, oldStatus });

    // Set timeout to actually save after 3 seconds
    undoTimeoutRef.current = setTimeout(async () => {
      try {
        await FeedbackService.updateFeedback(id, {
          status: newStatus,
          _oldStatus: oldStatus
        });
        setToast(null);
        setPendingCompletion(null);
      } catch (err) {
        console.error("Error updating status:", err);
        setError("Failed to update status");

        // Revert optimistic update on error
        setFeedback(prev =>
          prev.map(item =>
            item.id === id ? currentItem : item
          )
        );
        setToast(null);
        setPendingCompletion(null);
      }
    }, 3000); // 3 second delay
  };

  // Handle creating new feedback
  const handleCreateFeedback = async (formData) => {
    try {
      const result = await FeedbackService.createFeedback(formData);

      // Manually add to list if real-time doesn't catch it
      if (result) {
        setFeedback(prev => [result, ...prev]);
      }

      setShowNewModal(false);
      return result;
    } catch (err) {
      console.error("Error creating feedback:", err);
      setError("Failed to create feedback: " + (err.message || "Unknown error"));
      throw err;
    }
  };

  // Get badge variant for type (matches ContactsTable pattern)
  const getTypeBadgeVariant = (type) => {
    switch (type) {
      case 'Bug':
        return 'danger';
      case 'New Feature':
        return 'info';
      case 'UI Issue':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  // Get badge variant for priority (matches ContactsTable pattern)
  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'High':
        return 'danger';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'success';
      default:
        return 'neutral';
    }
  };

  // Column definitions for Table component
  const columns = [
    {
      id: 'status',
      label: 'Status',
      field: 'status',
      width: 155,
      minWidth: 140,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      contentType: 'dropdown',
      render: (value, row) => {
        const statusOptions = [
          { value: 'open', label: 'Open' },
          { value: 'in_progress', label: 'Working' },
          { value: 'completed', label: 'Completed' }
        ];

        const getStatusBadgeVariant = (status) => {
          switch (status) {
            case 'open': return 'neutral';
            case 'in_progress': return 'warning';
            case 'completed': return 'success';
            default: return 'neutral';
          }
        };

        return (
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%' }}>
            <Dropdown
              options={statusOptions}
              value={value || 'open'}
              onChange={(newStatus) => handleStatusChange(row.id, row.status, newStatus)}
              size="sm"
              placeholder="Set status"
            />
          </div>
        );
      },
    },
    {
      id: 'request',
      label: 'Request',
      field: 'request',
      width: 250,
      minWidth: 150,
      maxWidth: 500,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '—'}
        </div>
      ),
    },
    {
      id: 'details',
      label: 'Details / Loom Link',
      field: 'details',
      width: 300,
      minWidth: 150,
      maxWidth: 600,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '—'}
        </div>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      field: 'type',
      width: 120,
      minWidth: 100,
      maxWidth: 200,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) =>
        value ? (
          <Badge variant={getTypeBadgeVariant(value)} size="sm">
            {value}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'priority',
      label: 'Priority',
      field: 'priority',
      width: 100,
      minWidth: 90,
      maxWidth: 150,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) =>
        value ? (
          <Badge variant={getPriorityBadgeVariant(value)} size="sm">
            {value}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'created_at',
      label: 'Created',
      field: 'created_at',
      width: 100,
      minWidth: 90,
      maxWidth: 150,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value ? new Date(value).toLocaleDateString() : '—',
    },
    {
      id: 'created_by',
      label: 'Created By',
      field: 'employees.name',
      width: 130,
      minWidth: 100,
      maxWidth: 200,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value, row) => row.employees?.name || '—',
    },
  ];

  if (loading) {
    return <LoadingSpinner message="Loading feedback..." />;
  }

  return (
    <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ErrorMessage
            message={error}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            Feedback & Requests
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {openFeedback.length} open • {inProgressFeedback.length} working • {completedFeedback.length} completed
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={() => setShowNewModal(true)}
          icon={<MessageSquare size={18} />}
        >
          Add Request
        </Button>
      </div>

      {/* Active Filter Chips */}
      {(filters.type || filters.priority || filters.createdBy) && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
          padding: 'var(--space-3)',
          backgroundColor: 'var(--dark-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--gray-700)',
        }}>
          <span style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--gray-400)',
            fontWeight: 'var(--font-medium)',
            textTransform: 'uppercase',
          }}>
            Active filters:
          </span>
          {filters.type && (
            <FilterChip
              label={`Type: ${filters.type}`}
              onRemove={() => setFilters({ ...filters, type: '' })}
            />
          )}
          {filters.priority && (
            <FilterChip
              label={`Priority: ${filters.priority}`}
              onRemove={() => setFilters({ ...filters, priority: '' })}
            />
          )}
          {filters.createdBy && (
            <FilterChip
              label={`Created By: ${filters.createdBy}`}
              onRemove={() => setFilters({ ...filters, createdBy: '' })}
            />
          )}
        </div>
      )}

      {/* Open Feedback Section */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '85vh',
        marginBottom: 'var(--space-6)',
      }}>
        {/* Open Section Header */}
        <div
          onClick={toggleOpenSection}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-surface)',
            borderBottom: isOpenCollapsed ? 'none' : '1px solid var(--gray-600)',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            userSelect: 'none',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--gray-800)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--dark-surface)';
          }}
        >
          {isOpenCollapsed ? (
            <ChevronRight size={18} color="var(--gray-400)" />
          ) : (
            <ChevronDown size={18} color="var(--gray-400)" />
          )}
          <span style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-300)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Open ({openFeedback.length})
          </span>
        </div>

        {!isOpenCollapsed && (
          <>
        {/* Search Bar and Auto-resize Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search all feedback..."
              size="sm"
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={16} style={{ marginRight: 'var(--space-2)' }} />
                  Filters
                </Button>
              }
            >
              <div style={{
                padding: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                minWidth: '250px',
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Filter Feedback
                </h3>

                {/* Type Filter */}
                <Dropdown
                  label="Type"
                  value={filters.type}
                  options={[
                    { value: '', label: 'All types' },
                    ...uniqueTypes.map(type => ({ value: type, label: type }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, type: value })}
                />

                {/* Priority Filter */}
                <Dropdown
                  label="Priority"
                  value={filters.priority}
                  options={[
                    { value: '', label: 'All priorities' },
                    ...uniquePriorities.map(priority => ({ value: priority, label: priority }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, priority: value })}
                />

                {/* Created By Filter */}
                <Dropdown
                  label="Created By"
                  value={filters.createdBy}
                  options={[
                    { value: '', label: 'All users' },
                    ...uniqueCreatedBy.map(name => ({ value: name, label: name }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, createdBy: value })}
                  searchable
                />
              </div>
            </Popover>
            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={handleAutoResizeToggle}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div style={{
          backgroundColor: 'var(--dark-surface)',
          overflowX: 'auto',
          overflowY: 'auto',
          flex: 1,
          minWidth: 0,
        }}>
          <Table
            data={sortedOpenFeedback}
            columns={columns}
            sortable={true}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            searchable={false} // Using external search above
            resizable={true}
            paginated={false}
            autoResizeOnDataChange={autoResizeEnabled}
            showAutoSizeButton={false} // Hide built-in button, we have toggle
            storageKey="feedback-table-open"
            loading={false}
            emptyMessage={
              searchTerm
                ? 'No feedback found matching your search'
                : 'No open feedback. Click "Add Request" to create one.'
            }
            onRowClick={handleRowClick}
            onManualResize={handleManualResize} // Auto-disable auto-resize when user manually resizes
            variant="dense"
            stickyHeader={true}
            style={{ border: 'none', borderRadius: '0' }}
          />
        </div>
          </>
        )}
      </div>

      {/* Working/In-Progress Feedback Section */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '85vh',
        marginBottom: 'var(--space-6)',
      }}>
        {/* Working Section Header */}
        <div
          onClick={toggleWorkingSection}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-surface)',
            borderBottom: isWorkingCollapsed ? 'none' : '1px solid var(--gray-600)',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            userSelect: 'none',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--gray-800)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--dark-surface)';
          }}
        >
          {isWorkingCollapsed ? (
            <ChevronRight size={18} color="var(--gray-400)" />
          ) : (
            <ChevronDown size={18} color="var(--gray-400)" />
          )}
          <span style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-300)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Working ({inProgressFeedback.length})
          </span>
        </div>

        {!isWorkingCollapsed && (
          <>
        {/* Search Bar and Auto-resize Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search all feedback..."
              size="sm"
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={16} style={{ marginRight: 'var(--space-2)' }} />
                  Filters
                </Button>
              }
            >
              <div style={{
                padding: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                minWidth: '250px',
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Filter Feedback
                </h3>

                {/* Type Filter */}
                <Dropdown
                  label="Type"
                  value={filters.type}
                  options={[
                    { value: '', label: 'All types' },
                    ...uniqueTypes.map(type => ({ value: type, label: type }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, type: value })}
                />

                {/* Priority Filter */}
                <Dropdown
                  label="Priority"
                  value={filters.priority}
                  options={[
                    { value: '', label: 'All priorities' },
                    ...uniquePriorities.map(priority => ({ value: priority, label: priority }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, priority: value })}
                />

                {/* Created By Filter */}
                <Dropdown
                  label="Created By"
                  value={filters.createdBy}
                  options={[
                    { value: '', label: 'All users' },
                    ...uniqueCreatedBy.map(name => ({ value: name, label: name }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, createdBy: value })}
                  searchable
                />
              </div>
            </Popover>
            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={handleAutoResizeToggle}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div style={{
          backgroundColor: 'var(--dark-surface)',
          overflowX: 'auto',
          overflowY: 'auto',
          flex: 1,
          minWidth: 0,
        }}>
          <Table
            data={sortedInProgressFeedback}
            columns={columns}
            sortable={true}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            searchable={false}
            resizable={true}
            paginated={false}
            autoResizeOnDataChange={autoResizeEnabled}
            showAutoSizeButton={false}
            storageKey="feedback-table-working"
            loading={false}
            emptyMessage={
              searchTerm
                ? 'No feedback found matching your search'
                : 'No feedback currently being worked on.'
            }
            onRowClick={handleRowClick}
            onManualResize={handleManualResize}
            variant="dense"
            stickyHeader={true}
            style={{ border: 'none', borderRadius: '0' }}
          />
        </div>
          </>
        )}
      </div>

      {/* Completed Feedback Section */}
      {completedFeedback.length > 0 && (
        <div style={{
          border: '1px solid var(--gray-600)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
        }}>
          {/* Completed Section Header */}
          <div
            onClick={toggleCompletedSection}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-surface)',
              borderBottom: isCompletedCollapsed ? 'none' : '1px solid var(--gray-600)',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              userSelect: 'none',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--gray-800)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--dark-surface)';
            }}
          >
            {isCompletedCollapsed ? (
              <ChevronRight size={18} color="var(--gray-400)" />
            ) : (
              <ChevronDown size={18} color="var(--gray-400)" />
            )}
            <span style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-300)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Completed ({completedFeedback.length})
            </span>
          </div>

          {/* Completed Items Table */}
          {!isCompletedCollapsed && (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}>
              {/* Search Bar and Auto-resize Toolbar (same as active) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--dark-bg)',
                  borderBottom: '1px solid var(--gray-600)',
                  gap: 'var(--space-4)',
                  flexShrink: 0,
                }}
              >
                <div style={{ width: '300px', flexShrink: 0 }}>
                  <SearchBar
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search all feedback..."
                    size="sm"
                  />
                </div>
                <Button
                  variant={autoResizeEnabled ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
                >
                  {autoResizeEnabled ? '✓ ' : ''}Auto-resize
                </Button>
              </div>

              {/* Scrollable Table Container */}
              <div style={{
                backgroundColor: 'var(--dark-surface)',
                overflowX: 'auto',
                overflowY: 'auto',
                flex: 1,
                minWidth: 0,
              }}>
                <Table
                  data={sortedCompletedFeedback}
                  columns={columns}
                  sortable={true}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  searchable={false}
                  resizable={true}
                  paginated={false}
                  autoResizeOnDataChange={autoResizeEnabled}
                  showAutoSizeButton={false} // Hide built-in button, we have toggle
                  storageKey="feedback-table-completed"
                  loading={false}
                  emptyMessage="No completed feedback"
                  onRowClick={handleRowClick}
                  onManualResize={handleManualResize} // Auto-disable auto-resize when user manually resizes
                  variant="dense"
                  stickyHeader={true}
                  style={{
                    border: 'none',
                    borderRadius: '0',
                    opacity: 0.7,
                  }}
                  rowStyle={{
                    opacity: 0.7,
                    textDecoration: 'line-through',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Feedback Modal */}
      <NewFeedbackModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSave={handleCreateFeedback}
      />

      {/* Feedback Details Modal */}
      {showDetailsModal && selectedFeedback && (
        <FeedbackDetailsModal
          feedback={selectedFeedback}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedFeedback(null);
          }}
          initialEditMode={false}
          onFeedbackUpdated={(updatedFeedback) => {
            if (updatedFeedback === null) {
              // Feedback was deleted
              setFeedback(prev => prev.filter(item => item.id !== selectedFeedback.id));
            } else {
              // Feedback was updated
              setFeedback(prev =>
                prev.map(item => item.id === updatedFeedback.id ? updatedFeedback : item)
              );
            }
            setShowDetailsModal(false);
            setSelectedFeedback(null);
          }}
        />
      )}

      {/* Toast Notification with Undo */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 'var(--space-6)',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-elevated)',
            border: '1px solid var(--gray-600)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            zIndex: 9999,
            animation: 'slideUp 0.2s ease',
          }}
        >
          <CheckCircle2 size={20} color="var(--success)" />
          <span style={{ color: 'var(--gray-200)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
            {toast.message}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleUndo}
          >
            Undo
          </Button>
        </div>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default FeedbackTable;
